import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isUndefined, isNull, startCase } from 'lodash';
import { Container, Row, Col, Card, Button, Alert, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faMoneyCheckDollar, faPencil } from '@fortawesome/free-solid-svg-icons';
import { CopyLinkButton, Loading, LoadingError } from '@components/Base';
import { OrderStatuses } from '@models/Order';
import { OrderInvoiceLink } from '@components/Feature/Order';
import { selectIsCustomer, selectIsFrontline } from '@components/Auth';
import {
  QuoteBaseInfoCardBody,
  QuoteDocumentCardBody,
  QuoteExclusionCardBody,
  QuotePremiumChangeAlertCardBody,
} from '@components/Feature/Quote';
import { MemberCardBody } from '@components/HealthQuoteBuyRoute/HealthQuoteReviewPage/member-card-body';
import { RightCol } from '@components/HealthQuoteBuyRoute/right-col';

import { useCancerQuoteFetch } from '../hook';
import { cancerQuotePay, CancerQuotePayPayload } from '../action';
import { routeCancerQuoteBuyCustomPage, routeCancerQuoteBuyEditPage } from '../route';
import { CancerBenefitCardBody } from './cancer-benefit-card-body';
import { FeatureCardBody } from './feature-card-body';

const CancerQuoteReviewPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const cancerQuote = useCancerQuoteFetch();
  const isFrontline = useSelector(selectIsFrontline);
  const isCustomer = useSelector(selectIsCustomer);

  if (isUndefined(cancerQuote)) return <Loading />;
  if (isNull(cancerQuote)) return <LoadingError />;

  const {
    orderNumber,
    activationDate,
    status,
    validMedicalAnswers,
    orderEncryptedId,
    cancerPlanName,
  } = cancerQuote;
  const isPending = status === OrderStatuses.Pending;
  const isLocked = status === OrderStatuses.Locked;
  const isValidActivationDate = moment(activationDate).isAfter(moment(), 'day');
  const shouldEdit = isPending && isValidActivationDate;
  const shouldPay = isCustomer && (shouldEdit || isLocked);
  const shouldCustomPremium = isFrontline && isPending;

  const handlePay = () => {
    const payload: CancerQuotePayPayload = { orderNumber };
    dispatch(cancerQuotePay(payload));
  };

  return (
    <Container className="my-5">
      <Row>
        <Col lg={8}>
          <Card>
            <Card.Header className="d-flex flex-row justify-content-between align-items-center">
              {t('common:summary')}
              <CopyLinkButton />
            </Card.Header>

            <QuoteBaseInfoCardBody {...cancerQuote} />
            <QuotePremiumChangeAlertCardBody {...cancerQuote} />
            <FeatureCardBody {...cancerQuote} />
            <CancerBenefitCardBody {...cancerQuote} />
            <QuoteExclusionCardBody {...cancerQuote} />
            <QuoteDocumentCardBody {...cancerQuote} />
            <MemberCardBody {...cancerQuote} />
          </Card>
        </Col>

        <RightCol {...cancerQuote} planName={cancerPlanName}>
          <Card.Body>
            {!validMedicalAnswers && <Alert variant="warning">{t('common:policyUnderReview')}</Alert>}

            {isFrontline && (
              <div className="text-end">
                <OrderInvoiceLink orderEncryptedId={orderEncryptedId} />
              </div>
            )}

            <Stack direction="vertical" gap={2}>
              {shouldEdit && (
                <Button as={Link} to={routeCancerQuoteBuyEditPage({ orderNumber })} variant="outline-primary">
                  <FontAwesomeIcon icon={faPencil} className="me-2" />
                  {t('common:edit')}
                </Button>
              )}

              {shouldCustomPremium && (
                <Button as={Link} to={routeCancerQuoteBuyCustomPage({ orderNumber })} variant="secondary">
                  <FontAwesomeIcon icon={faMoneyCheckDollar} className="me-2" />
                  {t('common:custom')}
                </Button>
              )}

              {shouldPay && (
                <Button disabled={!validMedicalAnswers} onClick={handlePay}>
                  <span className="me-2">{startCase(t('common:payNow'))}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              )}
            </Stack>
          </Card.Body>
        </RightCol>
      </Row>
    </Container>
  );
};

export { CancerQuoteReviewPage };
